export interface BrandingResponse {
  app_title: string;
  /** #8: Gast-Share-Links sind nur im Showroom-Modus verfügbar — gated alle Sharing-UI. */
  sharing_enabled: boolean;
}

export interface VersionResponse {
  version: string;
  git_sha: string | null;
  build_date: string | null;
}

export interface MeResponse {
  user_id: string;
  email: string | null;
  display_name: string | null;
  roles: string[];
  groups: string[];
  effective_role: "user" | "operator" | "admin";
  is_admin: boolean;
  is_operator: boolean;
  auth_time: string;
  expires_at: string;
}

export type AppType = "proxy" | "external" | "managed";

export type RuntimeDesiredState = "running" | "stopped";
export type RuntimeObservedState =
  | "unknown"
  | "pulling"
  | "starting"
  | "healthy"
  | "unhealthy"
  | "stopped"
  | "failed";
export type DeploymentStatus =
  | "pending"
  | "pulling"
  | "starting"
  | "healthy"
  | "failed"
  | "rolled_back"
  | "stopped";

export interface AppRuntime {
  app_id: string;
  image_ref: string;
  pinned_digest: string | null;
  /** null = wird beim Deploy aus dem Image (EXPOSE) abgeleitet. */
  internal_port: number | null;
  env_json: string;
  /**
   * #69: Keys der at-rest verschlüsselten Secret-WERTE. Die Werte selbst und die
   * versiegelte Spalte verlassen den Hub nie — die API liefert ausschließlich
   * diese Key-Liste.
   */
  env_secret_keys: string[];
  volumes_json: string;
  cpu_limit: string | null;
  memory_limit_mb: number | null;
  healthcheck_path: string | null;
  desired_state: RuntimeDesiredState;
  observed_state: RuntimeObservedState;
  observed_at: string | null;
  /** #16: persistierter Update-Check-Status. */
  update_available: boolean;
  available_digest: string | null;
  update_checked_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AppDeployment {
  id: string;
  app_id: string;
  image_ref: string;
  image_digest: string | null;
  previous_digest: string | null;
  status: DeploymentStatus;
  triggered_by: string;
  triggered_by_display: string | null;
  trigger_kind: "manual" | "scheduled" | "auto-recover";
  log_excerpt: string | null;
  error: string | null;
  started_at: string;
  finished_at: string | null;
}

export interface RuntimeStatusResponse {
  runtime: AppRuntime | null;
  recent_deployments: AppDeployment[];
  metadata: AppMetadataFull | null;
}

export interface UpdateCheckResult {
  image_ref: string;
  current_digest: string | null;
  current_deployed_at: string | null;
  available_digest: string | null;
  update_available: boolean;
  image_pinned_to_digest: boolean;
  checked_at: string;
}

export interface EnvTemplateEntry {
  key: string;
  default_value: string | null;
  comment: string | null;
  is_secret_hint: boolean;
}

export interface EnvTemplateResult {
  image_ref: string;
  source_path: string | null;
  entries: EnvTemplateEntry[];
  probed_paths: string[];
}

/** Probe result for a package URL before an app exists (#34). */
export interface ImageProbeResult {
  image_ref: string;
  suggested_slug: string;
  suggested_name: string;
  suggested_port: number | null;
  env_template: EnvTemplateResult;
  /** #7: vom Image deklarierte VOLUME-Pfade; der Wizard schlaegt daraus Volumes vor. */
  volume_paths: string[];
  metadata: AppMetadataFull | null;
}

export interface OnboardManagedInput {
  slug: string;
  name: string;
  description?: string | null;
  category?: string | null;
  icon?: string | null;
  strip_prefix?: boolean;
  /** #38: Sichtbarkeit beim Anlegen (Default `public`). */
  visibility?: "private" | "public";
  access_rules?: { type: string; value?: string | null }[];
  proxy_settings?: Record<string, unknown>;
  runtime: {
    image_ref: string;
    internal_port?: number | null;
    env?: Record<string, string>;
    /** #100: Klartext-Secret-Werte; werden serverseitig versiegelt (AES-256-GCM). */
    env_secrets?: Record<string, string>;
    volumes?: Array<{ name: string; container_path: string; read_only?: boolean }>;
  };
  deploy?: boolean;
}

/** Server returns the full AppWithDetails plus the runtime row and deployment. */
export interface OnboardResult {
  app: { app: { id: string; slug: string; name: string } };
  runtime: AppRuntime;
  deployment: AppDeployment | null;
}

export type SelfRegistrationMode = "off" | "approval" | "open";

export interface HubSettingsResponse {
  self_registration: SelfRegistrationMode;
}

export interface RuntimeConfigInput {
  image_ref: string;
  pinned_digest?: string | null;
  /** Weglassen oder null = Hub liest den Port beim Deploy aus dem Image. */
  internal_port?: number | null;
  env?: Record<string, string>;
  /** #69: Klartext-Secret-Werte (upsert); werden serverseitig versiegelt. */
  env_secrets?: Record<string, string>;
  /** #69: Secret-Keys, die aus der verschlüsselten Map entfernt werden sollen. */
  env_secret_deletes?: string[];
  volumes?: Array<{ name: string; container_path: string; read_only?: boolean }>;
  cpu_limit?: string | null;
  memory_limit_mb?: number | null;
  healthcheck_path?: string | null;
  desired_state?: RuntimeDesiredState;
}

/** Image-derived metadata with no admin-owned column equivalent (issue #6). */
export interface AppMetadataPublic {
  vendor: string | null;
  version: string | null;
  documentation_url: string | null;
  homepage_url: string | null;
  source_url: string | null;
  brand: string | null;
  product: string | null;
  capabilities: string[];
}

/** Full image-derived metadata, surfaced on the runtime admin page. */
export interface AppMetadataFull extends AppMetadataPublic {
  title: string | null;
  description: string | null;
  icon: string | null;
  icon_dark: string | null;
  category: string | null;
  licenses: string[];
}

/** #35: schlanker Runtime-Status pro Managed App, wie ihn `/api/apps` mitliefert. */
export interface ApiAppRuntime {
  observed_state: RuntimeObservedState;
  desired_state: RuntimeDesiredState;
  update_available: boolean;
}

export interface ApiApp {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  app_type: AppType;
  public_path: string | null;
  target_url: string | null;
  icon: string | null;
  category: string | null;
  sort_order: number;
  metadata: AppMetadataPublic | null;
  // #38: Sichtbarkeit + Owner-/Favoriten-Flags für die Launchpad-Filter.
  visibility: "private" | "public";
  is_owner: boolean;
  is_favorite: boolean;
  // #6: Gast-Share-Status fürs Kachel-Icon. `shareable` = Capability;
  // `has_active_share_link` nur für Verwalter befüllt (sonst false).
  shareable: boolean;
  has_active_share_link: boolean;
  // #35: Runtime-Status für die Kachel; null bei nicht-Managed-Apps.
  runtime: ApiAppRuntime | null;
}

export interface AppsResponse {
  apps: ApiApp[];
}

export interface AdminApp extends ApiApp {
  upstream_url: string | null;
  strip_prefix: boolean;
  enabled: boolean;
  /** #123: App ist per Gast-Share-Link teilbar (nur im Showroom-Modus nutzbar). */
  shareable: boolean;
  /** #34: Lebenszyklus-Status. `pending` = wartet auf Admin-Freigabe. */
  status?: "active" | "pending";
  /** #16: aus dem gespeicherten Runtime-Stand; false für nicht-Managed-Apps. */
  update_available?: boolean;
  /** #36: Entra-Subject-ID des App-Erstellers; null bei Bestands-Apps ohne Backfill. */
  created_by?: string | null;
  /** #36: aufgelöster Klartextname des Owners; null wenn nicht auflösbar. */
  owner_display?: string | null;
  /** #36: E-Mail des Owners für den Teams-Deeplink; null wenn unbekannt. */
  owner_email?: string | null;
  created_at: string;
  updated_at: string;
  access_rules: { id: string; type: string; value: string | null }[];
  proxy_settings: {
    timeout_ms: number;
    max_body_mb: number;
    pass_identity_headers: boolean;
    pass_identity_jwt: boolean;
    cookie_rewrite: string;
    salesforce_required: boolean;
    salesforce_org_alias: string | null;
  };
}

export interface SalesforceOrg {
  alias: string;
  display_name: string;
  login_url: string;
  client_id: string;
  scopes: string[];
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface SalesforceOrgInput {
  alias: string;
  display_name: string;
  login_url: string;
  client_id: string;
  scopes: string[];
  enabled: boolean;
}

export interface SalesforceOrgPatch {
  display_name?: string;
  login_url?: string;
  client_id?: string;
  scopes?: string[];
  enabled?: boolean;
}

export interface SalesforceOrgsResponse {
  orgs: SalesforceOrg[];
}

export type GitHostProvider = "github" | "forgejo";

export interface GitHost {
  id: string;
  display_name: string;
  provider: GitHostProvider;
  repo_host: string | null;
  api_base_url: string | null;
  has_forge_token: boolean;
  registry_host: string | null;
  registry_username: string | null;
  has_registry_password: boolean;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface GitHostInput {
  id: string;
  display_name: string;
  provider: GitHostProvider;
  repo_host?: string;
  api_base_url?: string;
  forge_token?: string;
  registry_host?: string;
  registry_username?: string;
  registry_password?: string;
  enabled: boolean;
}

export interface GitHostPatch {
  display_name?: string;
  provider?: GitHostProvider;
  repo_host?: string;
  api_base_url?: string;
  forge_token?: string;
  registry_host?: string;
  registry_username?: string;
  registry_password?: string;
  enabled?: boolean;
}

export interface GitHostsResponse {
  hosts: GitHost[];
}

export interface AdminAppsResponse {
  apps: AdminApp[];
}

/** #123: Gast-Share-Token-Ansicht (ohne Secret). */
export interface ShareTokenView {
  id: string;
  app_id: string;
  label: string | null;
  created_by: string;
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
  last_used_at: string | null;
  status: "active" | "revoked" | "expired";
}

/** #123: Antwort beim Erzeugen — `url` enthält das Secret und wird nur einmal geliefert. */
export type CreatedShareToken = ShareTokenView & { url: string };

export interface AdminAuditEvent {
  id: string;
  actor_id: string;
  /** #36: aufgelöster Klartextname der handelnden Person; null wenn nicht auflösbar. */
  actor_display: string | null;
  action: string;
  entity_type: string;
  entity_id: string;
  before_json: string | null;
  after_json: string | null;
  created_at: string;
}

export interface AdminAuditResponse {
  events: AdminAuditEvent[];
}

export interface UsageSummaryRow {
  user_id: string;
  email: string | null;
  display_name: string | null;
  app_slug: string;
  call_count: number;
  total_duration_ms: number;
  first_used_at: string;
  last_used_at: string;
}

export interface AdminAnalyticsResponse {
  rows: UsageSummaryRow[];
}

export type ThresholdStatus = "ok" | "warn" | "critical";

export interface HealthResourceReport {
  totalBytes: number;
  usedBytes: number;
  freeBytes: number;
  usedPercent: number;
  status: ThresholdStatus;
}

export interface HealthCpuReport {
  load1: number;
  load5: number;
  load15: number;
  cores: number;
  usedPercent: number;
  status: ThresholdStatus;
}

export interface HealthAppReport {
  slug: string;
  name: string;
  cpuPercent: number | null;
  memUsageBytes: number | null;
  volumesBytes: number;
  writableBytes: number;
  totalBytes: number;
}

export interface HealthReportResponse {
  generated_at: string;
  host: {
    cpu: HealthCpuReport;
    memory: HealthResourceReport;
    disk: HealthResourceReport | null;
  };
  apps: HealthAppReport[];
  docker_available: boolean;
}

export interface UpstreamProbeResult {
  ok: boolean;
  status?: number;
  duration_ms: number;
  error?: string;
}

export interface AdminUserEntry {
  user_id: string | null;
  email: string | null;
  display_name: string | null;
  source: "bootstrap" | "granted";
  granted_by: string | null;
  granted_at: string | null;
  last_seen_at: string | null;
  can_revoke: boolean;
}

export interface PromotableEntry {
  user_id: string;
  email: string | null;
  display_name: string | null;
  last_seen_at: string;
}

export interface AdminUsersResponse {
  admins: AdminUserEntry[];
  promotable: PromotableEntry[];
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: string,
    readonly kind?: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: "same-origin", ...init });
  if (res.status === 401) throw new ApiError(401, "Authentication required");
  if (!res.ok) {
    let detail: string | undefined;
    let kind: string | undefined;
    try {
      const body = (await res.json()) as { error?: string; detail?: string; kind?: string };
      detail = body.detail ?? body.error;
      kind = body.kind;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, detail ?? `Request failed: ${res.status}`, detail, kind);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function mutate<T>(path: string, method: string, body?: unknown): Promise<T> {
  const init: RequestInit = {
    method,
    headers: { "content-type": "application/json" },
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  return request<T>(path, init);
}

export interface FeedbackImageInput {
  filename: string;
  mime: "image/png" | "image/jpeg" | "image/webp";
  data_base64: string;
}

export interface HubFeedbackInput {
  message: string;
  image?: FeedbackImageInput;
  context: { url: string; user_agent: string; logs: string[] };
}

export interface FeedbackResult {
  issue_url: string;
}

export const api = {
  branding: () => request<BrandingResponse>("/api/branding"),
  version: () => request<VersionResponse>("/api/version"),
  me: () => request<MeResponse>("/api/me"),
  submitHubFeedback: (input: HubFeedbackInput) =>
    mutate<FeedbackResult>("/api/feedback/hub", "POST", input),
  apps: () => request<AppsResponse>("/api/apps"),
  onboardingMode: () => request<{ mode: SelfRegistrationMode }>("/api/onboarding-mode"),
  checkSlug: (slug: string) =>
    request<{ available: boolean; reason: "ok" | "invalid" | "reserved" | "taken" }>(
      `/api/apps/check-slug?slug=${encodeURIComponent(slug)}`,
    ),
  probeImage: (image_ref: string) =>
    mutate<ImageProbeResult>("/api/apps/probe-image", "POST", { image_ref }),
  onboard: (input: OnboardManagedInput) =>
    mutate<OnboardResult>("/api/apps/onboard", "POST", input),
  // #38: Favoriten + Sichtbarkeit (Launchpad, owner/admin).
  favorite: (id: string) => mutate<void>(`/api/apps/${id}/favorite`, "POST", {}),
  unfavorite: (id: string) => mutate<void>(`/api/apps/${id}/favorite`, "DELETE"),
  setVisibility: (id: string, visibility: "private" | "public") =>
    mutate<{ id: string; visibility: "private" | "public" }>(
      `/api/apps/${id}/visibility`,
      "PATCH",
      { visibility },
    ),
  admin: {
    apps: () => request<AdminAppsResponse>("/api/admin/apps"),
    app: (id: string) => request<AdminApp>(`/api/admin/apps/${id}`),
    create: (input: unknown) => mutate<AdminApp>("/api/admin/apps", "POST", input),
    update: (id: string, patch: unknown) =>
      mutate<AdminApp>(`/api/admin/apps/${id}`, "PATCH", patch),
    delete: (id: string, keepVolumes = false) =>
      mutate<void>(
        `/api/admin/apps/${id}?keep_volumes=${keepVolumes ? "true" : "false"}`,
        "DELETE",
      ),
    test: (id: string) => mutate<UpstreamProbeResult>(`/api/admin/apps/${id}/test`, "POST"),
    probeImage: (image_ref: string) =>
      mutate<ImageProbeResult>("/api/admin/apps/probe-image", "POST", { image_ref }),
    onboard: (input: OnboardManagedInput) =>
      mutate<OnboardResult>("/api/admin/apps/onboard", "POST", input),
    approve: (id: string) => mutate<AdminApp>(`/api/admin/apps/${id}/approve`, "POST", {}),
    shareTokens: {
      list: (id: string) =>
        request<{ tokens: ShareTokenView[] }>(`/api/admin/apps/${id}/share-tokens`),
      create: (id: string, body: { ttl_minutes: number; label?: string }) =>
        mutate<CreatedShareToken>(`/api/admin/apps/${id}/share-tokens`, "POST", body),
      revoke: (id: string, tokenId: string) =>
        mutate<{ ok: true }>(`/api/admin/apps/${id}/share-tokens/${tokenId}/revoke`, "POST", {}),
    },
    settings: {
      get: () => request<HubSettingsResponse>("/api/admin/settings"),
      setSelfRegistration: (mode: SelfRegistrationMode) =>
        mutate<HubSettingsResponse>("/api/admin/settings/self-registration", "PUT", { mode }),
    },
    audit: (limit = 100) => request<AdminAuditResponse>(`/api/admin/audit?limit=${limit}`),
    analytics: (limit = 500) =>
      request<AdminAnalyticsResponse>(`/api/admin/analytics/summary?limit=${limit}`),
    health: () => request<HealthReportResponse>("/api/admin/system/health"),
    users: () => request<AdminUsersResponse>("/api/admin/users"),
    promote: (user_id: string) =>
      mutate<AdminUserEntry>(`/api/admin/users/${encodeURIComponent(user_id)}/promote`, "POST"),
    revoke: (user_id: string) =>
      mutate<void>(`/api/admin/users/${encodeURIComponent(user_id)}/promote`, "DELETE"),
    runtime: {
      get: (id: string) => request<RuntimeStatusResponse>(`/api/admin/apps/${id}/runtime`),
      configure: (id: string, body: RuntimeConfigInput) =>
        mutate<AppRuntime>(`/api/admin/apps/${id}/runtime`, "PUT", body),
      deploy: (id: string, body: { image_ref?: string; pin_digest?: string } = {}) =>
        mutate<AppDeployment>(`/api/admin/apps/${id}/runtime/deploy`, "POST", body),
      stop: (id: string) => mutate<void>(`/api/admin/apps/${id}/runtime/stop`, "POST", {}),
      start: (id: string) => mutate<void>(`/api/admin/apps/${id}/runtime/start`, "POST", {}),
      restart: (id: string) => mutate<void>(`/api/admin/apps/${id}/runtime/restart`, "POST", {}),
      rollback: (id: string) =>
        mutate<AppDeployment>(`/api/admin/apps/${id}/runtime/rollback`, "POST", {}),
      checkUpdate: (id: string) =>
        mutate<UpdateCheckResult>(`/api/admin/apps/${id}/runtime/check-update`, "POST", {}),
      // #103: Image in den Cache ziehen, ohne den Container neu zu starten.
      pull: (id: string) =>
        mutate<UpdateCheckResult>(`/api/admin/apps/${id}/runtime/pull`, "POST", {}),
      envTemplate: (id: string) =>
        request<EnvTemplateResult>(`/api/admin/apps/${id}/runtime/env-template`),
      logs: (id: string, params: { tail?: number; since?: string } = {}) => {
        const q = new URLSearchParams();
        if (params.tail !== undefined) q.set("tail", String(params.tail));
        if (params.since !== undefined) q.set("since", params.since);
        const suffix = q.toString();
        return request<{ logs: string }>(
          `/api/admin/apps/${id}/runtime/logs${suffix ? `?${suffix}` : ""}`,
        );
      },
      deployments: (id: string, limit = 20) =>
        request<{ deployments: AppDeployment[] }>(
          `/api/admin/apps/${id}/runtime/deployments?limit=${limit}`,
        ),
    },
    salesforce: {
      orgs: () => request<SalesforceOrgsResponse>("/api/admin/salesforce/orgs"),
      org: (alias: string) =>
        request<SalesforceOrg>(`/api/admin/salesforce/orgs/${encodeURIComponent(alias)}`),
      create: (input: SalesforceOrgInput) =>
        mutate<SalesforceOrg>("/api/admin/salesforce/orgs", "POST", input),
      update: (alias: string, patch: SalesforceOrgPatch) =>
        mutate<SalesforceOrg>(
          `/api/admin/salesforce/orgs/${encodeURIComponent(alias)}`,
          "PATCH",
          patch,
        ),
      delete: (alias: string) =>
        mutate<void>(`/api/admin/salesforce/orgs/${encodeURIComponent(alias)}`, "DELETE"),
    },
    gitHosts: {
      list: () => request<GitHostsResponse>("/api/admin/git-hosts"),
      get: (id: string) => request<GitHost>(`/api/admin/git-hosts/${encodeURIComponent(id)}`),
      create: (input: GitHostInput) => mutate<GitHost>("/api/admin/git-hosts", "POST", input),
      update: (id: string, patch: GitHostPatch) =>
        mutate<GitHost>(`/api/admin/git-hosts/${encodeURIComponent(id)}`, "PATCH", patch),
      delete: (id: string) =>
        mutate<void>(`/api/admin/git-hosts/${encodeURIComponent(id)}`, "DELETE"),
    },
  },
};
